import type { MenuObjectProps } from "../components/CollapsableMenu/types";
import {
  IconHelpFilled,
  IconIdCardFilled,
  IconLogout,
  IconMotorcycle,
  IconSquareMenu,
  IconUser,
  IconUtensils,
} from "../components/icons";
import { themeValues } from "../theme/themeValues";

export const DEFAULT_THEME = {
  ...themeValues,
  components: {
    ...themeValues.components,
    button: {
      ...themeValues.components.button,
      iconPadding: {
        large: 8,
        medium: 8,
        small: 4,
        tablet: 24,
        "tablet-medium": 16,
        "tablet-small": 8,
        totem: 32,
        "totem-medium": 24,
        "totem-small": 16,
      },
    },
  },
} as const;

export type DeviceType = "desktop" | "tablet" | "totem";

export type ButtonSize = "small" | "medium" | "large";

export type ColorShades = {
  white?: string;
  lightest: string;
  lighter: string;
  light: string;
  default: string;
  dark: string;
  darker: string;
  black?: string;
};

export type Colors = {
  [key in keyof typeof DEFAULT_THEME.colors]: ColorShades;
};

export type Spacing = {
  xxs: string;
  xs: string;
  s: string;
  m: string;
  l: string;
  xl: string;
  xxl: string;
};

export type Radius = {
  s: string;
  m: string;
  l: string;
  xl: string;
  xxl: string;
};

export type Typography = {
  family: {
    default: string;
  };
  weights: {
    regular: number;
    medium: number;
    semibold: number;
    bold: number;
  };
};

export type ButtonSizeKey =
  | ButtonSize
  | Exclude<DeviceType, "desktop">
  | `${Exclude<DeviceType, "desktop">}-${Exclude<ButtonSize, "large">}`;

export type InputSizeKey =
  | ButtonSize
  | "desktop"
  | Exclude<DeviceType, "desktop">
  | `${Exclude<DeviceType, "desktop">}-${Exclude<ButtonSize, "large">}`;

export type Components = {
  fieldInfo: {
    fontSize: Record<DeviceType, string>;
  };
  input: {
    label: {
      fontSize: Record<InputSizeKey, string>;
    };
    input: {
      fontSize: Record<InputSizeKey, string>;
      height: Record<InputSizeKey, string>;
      borderRadius: Record<InputSizeKey, string>;
      padding: Record<InputSizeKey, string>;
    };
  };
  select: {
    padding: Record<DeviceType, string>;
  };
  button: {
    fontSize: Record<ButtonSizeKey, string>;
    iconSize: Record<ButtonSizeKey, string>;
    height: Record<ButtonSizeKey, string>;
    borderRadius: Record<ButtonSizeKey, string>;
    padding: Record<
      ButtonSizeKey,
      {
        text: string;
        icon: string;
        textAndIcon: string;
      }
    >;
  };
};

export type DefaultThemeType = {
  colors: Colors;
  shadows: {
    lighter: string;
    light: string;
    default: string;
    dark: string;
    darker: string;
  };
  typography: Typography;
  spacing: Spacing;
  radius: Radius;
  components: Components;
  device: DeviceType;
};

export type ColorType = Exclude<keyof DefaultThemeType["colors"], "fixed">;

// Usado em alguns componentes como Switch e Radio Button
export const LABEL_POSITIONS = {
  top: "column",
  bottom: "column-reverse",
  left: "row",
  right: "row-reverse",
};

export const MENU_OBJECT: MenuObjectProps = {
  profile: {
    name: "Restaurante de teste",
    image: null,
    text: "Perfil",
    path: "/profile",
    icon: <IconUser />,
    displayType: "page",
    subitems: [
      { text: "Editar meu negócio", path: "/info" },
      { text: "Alterar perfil da conta", path: "/user-profile" },
      { text: "Tarefas", path: "/tasks" },
    ],
  },
  categories: [
    {
      text: "Operação",
      path: "/operation",
      icon: <IconUtensils />,
      displayType: "sidebar",
      subitems: [
        {
          text: "Mesa / Balcão",
          path: "",
        },
        {
          text: "Caixa",
          path: "/cashier",
        },
        {
          text: "Comandas",
          path: "/bills",
        },
        {
          text: "KDS",
          path: "/kds",
        },
      ],
    },
    {
      text: "Delivery",
      path: "/delivery",
      icon: <IconMotorcycle />,
      subitems: [
        {
          text: "Pedidos",
          path: "/orders",
          displayType: "sidebar",
        },

        {
          text: "Horário de funcionamento",
          path: "/opening-hours",
          displayType: "page",
        },
        {
          text: "Endereços",
          path: "/addresses",
          displayType: "page",
        },
        {
          text: "Agendamento",
          path: "/scheduling",
          displayType: "page",
        },
        {
          text: "Cupons",
          path: "/coupons",
          displayType: "page",
        },
        {
          text: "Motoboys",
          path: "/motoboys",
          displayType: "page",
        },
        {
          text: "Configurações",
          path: "/config",
        },
      ],
    },
    {
      text: "Cadastros",
      path: "/register",
      icon: <IconIdCardFilled />,
      displayType: "sidebar",
      subitems: [
        {
          text: "Cardápio",
          path: "/menu",
        },
        {
          text: "Estoque",
          path: "/stock",
          displayType: "sidebar",
        },
        {
          text: "Vinculos iFood ",
          path: "/ifood",
        },
        {
          text: "Vinculos Fiscais",
          path: "/fiscal",
        },
        {
          text: "Funcionários",
          path: "/employees",
          displayType: "page",
        },
        {
          text: "Impressoras e outros",
          path: "/printers",
          displayType: "page",
        },
      ],
    },
    {
      text: "Ver mais",
      path: "/search",
      icon: <IconSquareMenu />,
      displayType: "sidebar",
    },

    {
      text: "Fiscal",
      path: "/fiscal",
      displayType: "page",
      subitems: [
        { text: "Manifesto de Notas", path: "/manifest" },
        { text: "Entrada de Notas", path: "/entries" },
        { text: "Notas Emitidas", path: "/issued" },
        { text: "Notas Devolvidas", path: "/returned" },
        { text: "Grupos fiscais", path: "/groups" },
      ],
    },
    {
      text: "Estoque",
      path: "/register/stock",
      displayType: "page",
      subitems: [
        { text: "Insumos", path: "" },
        { text: "Transferência de Estoque", path: "/transfers" },
        { text: "Fornecedores", path: "/suppliers" },
      ],
    },
    {
      text: "Perfil",
      path: "/profile",
      displayType: "page",
      subitems: [
        { text: "Editar meu negócio", path: "/info" },
        { text: "Alterar perfil da conta", path: "/user-profile" },
        { text: "Tarefas", path: "/tasks" },
      ],
    },
    {
      text: "Ajuda",
      path: "/help",
      icon: <IconHelpFilled />,
      displayType: "sidebar",
      barPosition: "footer",
    },
    {
      text: "Sair",
      icon: <IconLogout />,
      displayType: "sidebar",
      barPosition: "footer",
      function: () => {
        console.log("Sair");
      },
    },
  ],
};

export const MENU_CONSTANTS = {
  TOOLTIPS: {
    CLOSE_MENU: "Fechar menu",
    OPEN_MENU: "Abrir menu",
  },
  SEARCH: {
    PLACEHOLDER: "Buscar",
    NO_RESULTS: "Nenhum resultado encontrado",
  },
} as const;

export const MENU_ITEMS = [
  {
    id: "home",
    text: "Home",
    path: "/",
  },
];
